import { CommonModule } from '@angular/common';
import { Component, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { EnumOptions } from '@kinet-open-network/shared/data-access/model';
import { SortMeta } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { IndicatorComponent, ProcessedAction, TableAction } from '../..';
import { EnumColumnComponent } from '../enum-column/enum-column.component';

export interface SimpleTableColumn<T> {
  field: keyof T | string;
  header: string;
  class?: string;
  options?: Map<string, EnumOptions>;
  type?: 'string' | 'number' | 'date' | 'enum';
  tooltip?: (value: any, row: any) => string;
  icon?: (value: any, row: any) => string;
  mapValue?: (value: any, row: any) => string;
  pipeFormat?: string;
  routerLink?: (value: T) => any[] | string;
  indicator?: boolean;
}

@Component({
  selector: 'kon-simple-table',
  imports: [
    TableModule,
    EnumColumnComponent,
    CommonModule,
    TooltipModule,
    ButtonModule,
    RouterLink,
    TooltipModule,
    IndicatorComponent,
  ],
  template: `
    <p-table
      [value]="data() ?? []"
      [loading]="loading()"
      [columns]="columns()"
      sortMode="multiple"
      [multiSortMeta]="defaultSort()"
    >
      <ng-template pTemplate="header" let-columns>
        <tr>
          <th *ngFor="let col of columns" [pSortableColumn]="col.field">
            <div class="flex flex-row flex-nowrap items-center">
              <span>{{ col.header }}</span> <p-sortIcon [field]="col.field" />
            </div>
          </th>
          <th *ngIf="rowActions()"></th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-rowData let-columns="columns">
        <tr class="group/row hover:bg-gray-50">
          <td
            *ngFor="let col of columns"
            [ngClass]="'text-nowrap ' + col.class"
            [pTooltip]="
              col.tooltip ? col.tooltip(rowData[col.field], rowData) : undefined
            "
          >
            @switch (col.type ?? 'string') { @case('date') {
            {{
              rowData[col.field] | date : col.pipeFormat || 'dd.MM.yyyy HH:mm'
            }}} @case('number') {
            {{ rowData[col.field] | number : col.pipeFormat || '1.2-2' }}}
            @case('enum') { @if(col?.indicator){
            <kon-indicator
              [options]="col.options"
              [value]="rowData[col.field]"
            />
            }@else {
            <kon-enum-column
              *ngIf="!!rowData[col.field] && col.options"
              [value]="rowData[col.field]"
              [options]="col.options"
            />
            } } @default {
            {{
              col.mapValue
                ? col.mapValue(rowData[col.field], rowData)
                : rowData[col.field]
            }}} } @if(col.icon){
            <i
              *ngIf="!rowData[col.field]"
              [class]="col.icon(rowData[col.field], rowData)"
            ></i>
            }
          </td>
          <td
            *ngIf="rowActions()"
            class="'!px-3 !py-0.5 bg-gray-50 sm:sticky sm:right-0 sm:opacity-0 sm:group-hover/row:opacity-100"
          >
            <div class="flex items-center justify-end">
              <ng-container *ngFor="let action of processActions(rowData)">
                <button
                  *ngIf="action"
                  pButton
                  type="button"
                  [ngClass]="[
                    'row-action-button',
                    'hidden',
                    'sm:flex',
                    'p-button-sm',
                    'p-button-text',
                    'p-button-' + (action.color ?? 'primary'),
                    'p-button-rounded',
                    action.styleClass ?? ''
                  ]"
                  [icon]="action.icon ?? ''"
                  [label]="action.showRowLabel ? action.label ?? '' : ''"
                  [routerLink]="action.routerLink"
                  [queryParams]="action.queryParams"
                  [pTooltip]="action.label"
                  [disabled]="action.disabled"
                  [tooltipPosition]="'bottom'"
                  [tooltipOptions]="{ showDelay: 300 }"
                  (click)="$event.stopPropagation(); action.command?.()"
                ></button>
              </ng-container>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  `,
  standalone: true,
})
export class SimpleTableComponent<T> {
  data = input<T[]>();
  columns = input<SimpleTableColumn<T>[]>();
  loading = input<boolean>();
  defaultSort = input<SortMeta[]>();
  rowActions = input<TableAction<T>[]>();
  processActions = (row: T) => {
    const actions = this.rowActions();
    if (!actions) {
      return undefined;
    }

    return actions.map((action) => {
      const functionLikes: (keyof TableAction<T>)[] = ['command'];

      const processed = Object.fromEntries(
        Object.entries(action).map(([k, v]) => {
          // if the value is a function, we need to call it with the row
          if (typeof v === 'function') {
            // we need to wrap the function inside another function to make sure it's not called immediately
            if (functionLikes.includes(k as keyof TableAction<T>)) {
              return [k, () => v(row)];
            }

            return [k, v(row)];
          }

          return [k, v];
        }),
      ) as ProcessedAction<T>;

      if (processed.visible === false) {
        return undefined;
      }

      return processed;
    });
  };
}
